import { useState } from "react";
import { RefreshCw, X, Info } from "lucide-react";
import { useVersionCheck, VersionDetail } from "@/hooks/useVersionCheck";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function VersionEntry({ version }: { version: VersionDetail }) {
  return (
    <div className="border-b border-gray-200 last:border-b-0 pb-3 last:pb-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-gray-900">
          v{version.version_number}
        </span>
        <span className="text-xs text-gray-500">
          {formatDate(version.released_at)}
        </span>
      </div>
      {version.description.length > 0 ? (
        <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 pl-1">
          {version.description.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-400 italic pl-1">
          No description provided
        </p>
      )}
    </div>
  );
}

export function UpdateBanner() {
  const { isOutdated, missedVersions, dismiss } = useVersionCheck();
  const [showDetails, setShowDetails] = useState(false);

  if (!isOutdated) return null;

  return (
    <>
      <div className="flex items-center justify-between gap-3 rounded-md bg-blue-50 border border-blue-200 px-4 py-2 mb-2 absolute top-4 right-4">
        <div className="flex items-center gap-2 min-w-0">
          <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <span className="text-sm text-blue-800">
            A new version of the portal is available.
          </span>
          {missedVersions.length > 0 && (
            <button
              onClick={() => setShowDetails(true)}
              className="text-sm text-blue-600 underline underline-offset-2 hover:text-blue-800 flex-shrink-0"
            >
              What's new?
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
          <button
            onClick={dismiss}
            className="text-blue-400 hover:text-blue-600 p-0.5"
            aria-label="Dismiss update notification"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>What's New</DialogTitle>
            <DialogDescription>
              {missedVersions.length === 1
                ? "A new version has been deployed:"
                : `${missedVersions.length} versions have been deployed since your last refresh:`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-3 py-2 pr-1">
            {missedVersions.map((version) => (
              <VersionEntry key={version.hash} version={version} />
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetails(false)}
            >
              Close
            </Button>
            <Button
              size="sm"
              onClick={() => window.location.reload()}
              className="gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
